import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, User, MapPin, ClipboardList, History, RefreshCw, XCircle, AlertTriangle, Paperclip, FileText, Trash2 } from "lucide-react";
import { PageLoading, fmtDateTime } from "@/components/shared";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const STATUS_C = { DRAFT: "bg-slate-100 text-slate-500", SCHEDULED: "bg-cyan/10 text-cyan-dark", READY: "bg-emerald-50 text-emerald-600", IN_PROGRESS: "bg-blue-50 text-blue-600", COMPLETED: "bg-slate-100 text-slate-500", CANCELLED: "bg-red-50 text-red-600", LATE: "bg-amber-50 text-amber-700" };
const Info = ({ label, value }) => (<div className="flex justify-between border-b border-slate-100 py-2 text-sm last:border-0"><span className="text-slate-500">{label}</span><span className="font-semibold text-navy">{value || "—"}</span></div>);
const Card = ({ title, icon: Icon, children, testid }) => (<div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={testid}><div className="mb-3 flex items-center gap-2"><Icon className="h-4.5 w-4.5 text-cyan-dark" /><h3 className="font-heading font-bold text-navy">{title}</h3></div>{children}</div>);

export default function CareVisitDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [workers, setWorkers] = useState([]);
  const nav = useNavigate();

  const [atts, setAtts] = useState([]);
  const [previews, setPreviews] = useState({});
  const previewsRef = useRef({});
  useEffect(() => { previewsRef.current = previews; }, [previews]);
  useEffect(() => () => { Object.values(previewsRef.current).forEach((u) => URL.revokeObjectURL(u)); }, []);
  const load = useCallback(() => api.get(`/care/visits/${id}`).then((r) => setD(r.data)).catch(() => setD(false)), [id]);
  const loadAtts = useCallback(() => api.get(`/care/visits/${id}/attachments`).then((r) => setAtts(r.data.attachments || [])).catch(() => setAtts([])), [id]);
  useEffect(() => { load(); loadAtts(); api.get("/care/workers").then((r) => setWorkers(r.data.workers)).catch(() => {}); }, [load, loadAtts]);
  useEffect(() => {
    atts.filter((a) => (a.content_type || "").startsWith("image/")).forEach(async (a) => {
      if (previews[a.id]) return;
      try { const r = await api.get(`/documents/${a.id}/download`, { responseType: "blob" }); setPreviews((p) => ({ ...p, [a.id]: URL.createObjectURL(r.data) })); } catch { /* ignore */ }
    });
  }, [atts]); // eslint-disable-line react-hooks/exhaustive-deps
  const openAttachment = async (a) => {
    try { const r = await api.get(`/documents/${a.id}/download`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); } catch { toast.error("Download fehlgeschlagen"); }
  };
  const deleteAttachment = async (a) => {
    if (!window.confirm(`Anhang "${a.filename}" wirklich löschen?`)) return;
    try { await api.delete(`/care/visits/${id}/attachments/${a.id}`); toast.success("Anhang gelöscht"); loadAtts(); } catch { toast.error("Löschen fehlgeschlagen"); }
  };
  if (d === false) return <div className="py-20 text-center text-slate-400">Besuch nicht gefunden.</div>;
  if (!d) return <PageLoading />;
  const v = d.visit;
  const started = ["IN_PROGRESS", "COMPLETED"].includes(v.status);

  const assign = async (wid) => {
    const r = await api.post(`/care/visits/${id}/assign`, { assigned_worker_id: wid || null });
    if (r.data.warnings?.length) r.data.warnings.forEach((w) => toast.warning(w));
    if (r.data.conflicts?.length) toast.error(`Terminüberschneidung mit ${r.data.conflicts.length} Besuch(en)`);
    else toast.success("Zuweisung gespeichert");
    load();
  };
  const refresh = async () => { const r = await api.post(`/care/visits/${id}/refresh-snapshot`); toast.success(`Snapshot aktualisiert (${r.data.task_count} Aufgaben)`); load(); };
  const cancel = async () => { const reason = window.prompt("Grund für Stornierung:"); if (!reason) return; await api.post(`/care/visits/${id}/cancel`, { reason }); toast.success("Besuch storniert"); load(); };

  return (
    <div data-testid="care-visit-detail">
      <button onClick={() => nav("/app/employer/pflege/visits")} className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-navy"><ArrowLeft className="h-4 w-4" /> Alle Besuche</button>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_C[v.status] || "bg-slate-100 text-slate-500"}`} data-testid="visit-status">{v.status_label}</span>
            <span className="font-mono text-xs text-slate-400">{v.visit_number}</span>
          </div>
          <h1 className="mt-2 font-heading text-2xl font-extrabold text-navy">{d.patient?.name}</h1>
          <div className="mt-1 text-sm text-slate-500">{v.scheduled_date} · {v.scheduled_start}–{v.scheduled_end} · {v.visit_type_label}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          {!started && v.status !== "CANCELLED" && <Button variant="outline" className="rounded-full text-red-600" onClick={cancel} data-testid="visit-cancel"><XCircle className="mr-1 h-4 w-4" /> Stornieren</Button>}
        </div>
      </div>

      {d.newer_plan_available && !started && <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-amber-50 p-4 text-sm text-amber-800" data-testid="visit-newer-plan">
        <span className="flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Neuere Pflegeplan-Version verfügbar.</span>
        <Button size="sm" className="rounded-full bg-amber-600 text-white hover:bg-amber-700" onClick={refresh} data-testid="visit-refresh-snapshot"><RefreshCw className="mr-1 h-4 w-4" /> Besuch aktualisieren</Button>
      </div>}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Zuweisung" icon={User} testid="visit-assignment">
          <Info label="Mitarbeiter/in" value={d.worker?.name} />
          <Info label="Rolle" value={d.worker?.care_role} />
          <Info label="Zwei-Personen" value={v.secondary_worker_required ? "Ja" : "Nein"} />
          {!started && v.status !== "CANCELLED" && <label className="mt-2 block"><span className="mb-1 block text-xs font-semibold text-slate-500">Zuweisen / ändern</span>
            <select value={v.assigned_worker_id || ""} onChange={(e) => assign(e.target.value)} data-testid="visit-assign-select" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm">
              <option value="">— nicht zugewiesen —</option>{workers.map((w) => <option key={w.id} value={w.id}>{w.name} ({w.care_role_label})</option>)}
            </select></label>}
        </Card>

        <Card title="Besuchsort" icon={MapPin} testid="visit-location">
          {d.location ? <>
            <Info label="Adresse" value={[d.location.street, d.location.house_number].filter(Boolean).join(" ")} />
            <Info label="Ort" value={`${d.location.postal_code || ""} ${d.location.city || ""}`.trim()} />
            <Info label="Zugang" value={d.location.access_instructions} />
          </> : <div className="text-sm text-red-500">Kein Besuchsort hinterlegt.</div>}
        </Card>

        <Card title={`Aufgaben-Snapshot (V${v.task_snapshot_version})`} icon={ClipboardList} testid="visit-tasks">
          <div className="space-y-2">
            {d.task_snapshot.map((t, i) => (
              <div key={t.id} className="rounded-xl bg-slate-50 p-3 text-sm" data-testid={`visit-task-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-navy">{i + 1}. {t.title}</span>
                  <span className="rounded-full bg-cyan/10 px-2 py-0.5 text-[10px] font-semibold text-cyan-dark">{d.meta.task_types[t.task_type] || t.task_type}</span>
                </div>
                {t.instructions && <div className="mt-1 text-xs text-slate-500">{t.instructions}</div>}
                {t.role_requirement !== "ANY" && <span className="mt-1 inline-block rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700">{t.role_requirement}</span>}
              </div>
            ))}
            {d.task_snapshot.length === 0 && <div className="text-sm text-slate-400">Keine Aufgaben im Snapshot.</div>}
          </div>
        </Card>

        <Card title="Verlauf" icon={History} testid="visit-timeline">
          <div className="space-y-2">
            {d.timeline.map((t) => (
              <div key={t.id} className="flex items-start gap-2 text-sm">
                <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-cyan" />
                <div><span className="font-semibold text-navy">{t.event}</span><div className="text-xs text-slate-400">{fmtDateTime(t.created_at)} · {t.actor_name}</div></div>
              </div>
            ))}
          </div>
          <div className="mt-3 border-t border-slate-100 pt-3 text-xs text-slate-400">Platzhalter (Phase 9+): Ist-Start/Ende, Standortprüfung, NFC-Quelle, Fahrtdetails.</div>
        </Card>

        <Card title="Anhänge / Fotos" icon={Paperclip} testid="visit-attachments">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {atts.map((a) => (
              <div key={a.id} className="group relative overflow-hidden rounded-xl border border-slate-200 transition-shadow hover:shadow-md" data-testid={`visit-att-${a.id}`}>
                <button onClick={() => deleteAttachment(a)} className="absolute right-1.5 top-1.5 z-10 rounded-full bg-white/90 p-1 text-red-500 shadow hover:bg-red-50" title="Löschen" data-testid={`visit-att-delete-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                <button onClick={() => openAttachment(a)} className="block w-full text-left">
                  {previews[a.id]
                    ? <img src={previews[a.id]} alt={a.filename} className="h-24 w-full object-cover" />
                    : <div className="flex h-24 w-full items-center justify-center bg-slate-50 text-slate-400"><FileText className="h-8 w-8" /></div>}
                  <div className="p-2"><div className="truncate text-xs font-semibold text-navy">{a.filename}</div>{a.note && <div className="truncate text-[10px] text-slate-400">{a.note}</div>}</div>
                </button>
              </div>
            ))}
            {atts.length === 0 && <div className="col-span-full py-4 text-center text-sm text-slate-400">Keine Anhänge.</div>}
          </div>
        </Card>
      </div>
    </div>
  );
}
